//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { InputAdornment, makeStyles } from '@material-ui/core';
import { SearchOutlined } from '@material-ui/icons';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { PLACEHOLDER_JOB_TITLE_SKILLSET_KEYWORDS, PLACEHOLDER_LOCATION, PLACEHOLDER_OCCUPATION_SKILLSET_KEYWORDS } from '../../common/placeholderHelper';
import { getNewJobCenterQuery, isNonLocationFiltersExistsInQuery, isQueryEmpty } from '../../common/queryHelper';
import { ELASTICSEARCH_SORT_OPTIONS, ELASTICSEARCH_SORT_OPTION_DISTANCE_ASC, ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT } from '../../common/elasticSearchHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { getJobTitleSuggestions, getLocationSuggestions } from '../../common/services/jobCenterServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdAutocompleteLite from '../../common/elemental/AfitdAutocompleteLite';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocomplete';
import AfitdIconButton from '../../common/elemental/AfitdIconButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import clsx from 'clsx';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        searchInput: {
            display: 'flex',
            borderRadius: '4px',
            background: '#F0F2F6',
            alignItems: 'center',
            padding: '5px',
            border: '2px solid #F0F2F6',
        },
        searchInputFocus: {
            background: '#fff',
            border: '2px solid #1D8A8B',
        },
        iconContainer: {
            cursor: 'pointer',
            width: 'auto',
            // marginRight: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        icon: {
            color: '#9B9E9C',
        },
        iconActive: {
            color: '#9B9E9C',
        },
        inputSide: {
            flex: 1,
            borderRight: '1px solid #D5D7DD',

            '& fieldset': {
                display: 'none',
            },
        },
        inputSideAddress: {
            flex: 1,
            '& fieldset': {
                border: 'none',
            },
            marginLeft: 8,
        },
        dropDown: {
            '& .MuiAutocomplete-popupIndicator': {
                display: 'none',
            },
            '& .MuiAutocomplete-input': {
                fontSize: '16px',
                fontWeight: '600',
                color: '#1D8A8B',
            },
            '& .MuiAutocomplete-input::placeholder': {
                fontWeight: '400',
                color: '#222222',
            },

            '& .MuiAutocomplete-inputRoot': {
                paddingLeft: 10,
                borderRadius: '0px',
            },
            '& .MuiInputBase-root': {
                padding: '0 !important',
            },
            '& .MuiInputBase-root input': {
                marginRight: '10px',
            },
        },
        addressDropDown: {
            '& .MuiAutocomplete-endAdornment': {
                right: '-15px !important',
            },
            '& .MuiAutocomplete-popupIndicator': {
                display: 'none',
            },
            '& .MuiAutocomplete-input': {
                fontSize: '16px',
                fontWeight: '600',
                color: '#1D8A8B',
            },
            '& .MuiAutocomplete-input::placeholder': {
                fontWeight: '400',
                color: '#222222',
            },

            '& .MuiAutocomplete-inputRoot': {
                paddingLeft: 10,
                borderRadius: '0px',
            },
            '& .MuiInputBase-root': {
                padding: '0 !important',
            },
            '& .MuiInputBase-root input': {
                marginRight: '10px',
            },
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        appContext,
        props,
        searchFocus,
        setSearchFocus,
        addressFocus,
        setAddressFocus,
        handleChangeJobTitle,
        handleChangeLocation,
        handleFocusLocationInput,
        handleChangeJobTitleInput,
        handleClickSearchIconButton,
        jobTitleOptions,
        setJobTitleOptions,
        locationOptions,
        setLocationOptions,
    } = params;

    const { searchInput: searchInputCls, searchInputFocus: searchFocusCls, dropDown: dropDownCls, icon: IconCls, iconActive: iconActiveCls, addressDropDown } = classes;
    const { query } = props;

    const { esToken, setEsToken } = appContext;

    const defns = {
        icon: {
            className: clsx(IconCls, { [iconActiveCls]: searchFocus || addressFocus }),
        },
        searchIconButton: { onClick: handleClickSearchIconButton },
        root: {
            className: clsx(searchInputCls, { [searchFocusCls]: searchFocus || addressFocus }),
        },
        leftInput: {
            freeSolo: true,
            autoSelect: false,
            autoHighlight: false,
            value: query?.jobTitle,
            onChange: handleChangeJobTitle,
            onBlur: () => setSearchFocus(false),
            onFocus: () => setSearchFocus(true),
            placeholder: PLACEHOLDER_OCCUPATION_SKILLSET_KEYWORDS,
            title: PLACEHOLDER_JOB_TITLE_SKILLSET_KEYWORDS,
            className: dropDownCls,
            options: jobTitleOptions, //Options.map(el => el.label),
            onInputChange: handleChangeJobTitleInput,
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getJobTitleSuggestions({ keywords: value, esToken, setEsToken });
                    setJobTitleOptions(options);
                } else setJobTitleOptions([]);
            },
        },
        rightInput: {
            value: query?.location,
            onChange: handleChangeLocation,
            onBlur: () => setAddressFocus(false),
            onFocus: handleFocusLocationInput,
            // onFocus: () => setAddressFocus(true),
            placeholder: PLACEHOLDER_LOCATION,
            title: PLACEHOLDER_LOCATION,
            className: addressDropDown,
            options: locationOptions, //Address, //Address.map(el => el.label),
            getOptionSelected: (option, value) => option?.label === value?.label,
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getLocationSuggestions({ keywords: value, esToken, setEsToken });
                    setLocationOptions(options);
                } else setLocationOptions([]);
            },
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SearchInput = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { esToken, setEsToken } = appContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { isHidden, query, setQuery, setSortTag } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [searchFocus, setSearchFocus] = useState(false);
    const [addressFocus, setAddressFocus] = useState(false);
    const [jobTitleOptions, setJobTitleOptions] = useState();
    const [locationOptions, setLocationOptions] = useState([]);
    const [jobTitleInputText, setJobTitleInputText] = useState();

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // load default job title suggestions if it has not been done so
    useEffect(() => {
        const loadInitialJobTitleSuggestions = async () => {
            //TODO Get the business rule for "default suggestions" for job title
            const options = await getJobTitleSuggestions({ keywords: 'office', esToken, setEsToken });
            setJobTitleOptions(options);
        };
        if (_isNil(jobTitleOptions)) {
            loadInitialJobTitleSuggestions();
        }
    }, [jobTitleOptions, setJobTitleOptions, esToken, setEsToken]);

    // clear suggestions if query are empty
    useEffect(() => {
        if (isQueryEmpty(query)) {
            setJobTitleOptions([]);
            setLocationOptions([]);
        }
    }, [query]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const nonLocationFiltersExistsInQuery = isNonLocationFiltersExistsInQuery(query);
    const locationQueryLabel = _get(query, 'location.label');

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeJobTitle = useCallback(
        (evt, jobTitle = {}) => {
            setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { jobTitle } }));
        },
        [setQuery],
    );
    const handleChangeLocation = useCallback(
        (evt, option = {}) => {
            // if lat/long exists but no other filters exists, sort it by Distance
            const lat = _get(option, 'value.location.lat');
            const lon = _get(option, 'value.location.lon');
            const latLonExists = lat && lon;

            // update the 'Sort by' field on the UI
            if (latLonExists && !nonLocationFiltersExistsInQuery) {
                setSortTag(ELASTICSEARCH_SORT_OPTION_DISTANCE_ASC);
            } else if (!latLonExists) {
                setSortTag(ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT);
            }

            // update the query
            setQuery(prevQuery => {
                const prevLat = _get(prevQuery, 'location.value.location.lat');
                const prevLon = _get(prevQuery, 'location.value.location.lon');
                const prevLatLonExists = prevLat && prevLon;

                // reset sort order base on whether lat/lon exists in previous and current queries
                let sortTagDto = null;
                if (prevLatLonExists && !latLonExists) {
                    sortTagDto = ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT];
                } else if (!prevLatLonExists && latLonExists) {
                    sortTagDto = ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_DISTANCE_ASC];
                }

                return getNewJobCenterQuery({ prevQuery, newQuery: { location: option, sortTag: sortTagDto } });
            });
        },
        [nonLocationFiltersExistsInQuery, setQuery, setSortTag],
    );
    const handleFocusLocationInput = useCallback(async () => {
        let options = [];
        if (locationQueryLabel) {
            const options = await getLocationSuggestions({ keywords: locationQueryLabel, esToken, setEsToken });
            setLocationOptions(options);
        } else {
            setLocationOptions(options);
            setAddressFocus(true);
        }
    }, [setLocationOptions, setAddressFocus, locationQueryLabel, esToken, setEsToken]);

    const handleChangeJobTitleInput = useCallback((evt, value) => {
        setJobTitleInputText(value);
    }, []);

    const handleClickSearchIconButton = useCallback(() => {
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { jobTitle: jobTitleInputText } }));
    }, [jobTitleInputText, setQuery]);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    if (isHidden) return null;
    const defns = getDefns({
        classes,
        appContext,
        props,
        searchFocus,
        setSearchFocus,
        addressFocus,
        setAddressFocus,
        handleChangeJobTitle,
        handleChangeLocation,
        handleFocusLocationInput,
        handleChangeJobTitleInput,
        handleClickSearchIconButton,
        jobTitleOptions,
        setJobTitleOptions,
        locationOptions,
        setLocationOptions,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.inputSide}>
                <AfitdAutocompleteLite {...defns.leftInput} />
            </div>
            <div {...defns.inputSideAddress}>
                <AfitdAutocomplete {...defns.rightInput} />
            </div>
            <div {...defns.iconContainer}>
                <InputAdornment position="end">
                    <AfitdIconButton {...defns.searchIconButton}>
                        <SearchOutlined {...defns.icon} />
                    </AfitdIconButton>
                </InputAdornment>
            </div>
        </div>
    );
};

export default SearchInput;
